import React from 'react';
import { ChakraProvider, theme } from '@chakra-ui/react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Login from './layout/login';
import Register from './layout/register';
import NraRegister from './layout/nraRegister';
import Dashboard from './layout/dashboard';
import ReskinnedDashboard from './layout/ReskinnedDashboard';
import CallbackHandler from './components/callback/handleCallback';
import OktaCallbackHandler from './components/callback/OktaCallbackHandler';
import ResetPassword from './components/login/resetPasswordComponent';
import AISummaryOneDemand from './components/widgets/aiSummary/aiSummary_ondemand';
import RegistrationConfirmation from './components/register/RegistrationConfirmation';
import UserInitializer from './components/auth/UserInitializer';
import TokenRefreshHandler from './components/auth/TokenRefreshHandler';
import WidgetDrawerWrapper from './components/widgets/WidgetDrawerWrapper';
import NavigateWithQuery from './components/routing/NavigateWithQuery';
import { reskinnedRoutes } from './config/routes';
import LaunchDarklyUserIdentifier from './components/auth/LaunchDarklyUserIdentifier';
import HeapUserIdentifier from './components/auth/HeapUserIdentifier';
import PageNavigationTracker from './components/analytics/PageNavigationTracker';
import { NexusProvider } from '@spotoninc/nexus-react';
import { useFeatureFlagFromContext } from './launchdarkly';
import { useEffect } from 'react';
import { initializeHeapAnalytics } from './utils/analytics';
// Import custom styles
import './styles/nexus-overrides.css';
import './styles/reskinned-navbar.css';
import './styles/reskinned-sidebar.css';
import './styles/content-layout-fix.css';
import './styles/action-menu-fix.css';
import './styles/modal-fix.css';
import './styles/max-width-container.css';
import './styles/conditional-picker-styles.css';
import spotonLogo from './media/images/spoton.png';

function App() {
  const showReskin = useFeatureFlagFromContext('showReskin', false); // Get boolean value from showReskin flag
  const pageTitle = showReskin ? 'Profit Assist' : 'Starfish';
  const favicon = showReskin ? spotonLogo : '/static/Emoticon.png';

  useEffect(() => {
    // Update page title
    document.title = pageTitle;

    // Update favicon
    const linkElement = document.querySelector("link[rel='icon']");
    if (linkElement) {
      linkElement.href = favicon;
    }
  }, [pageTitle, favicon]);

  // Initialize Heap Analytics on app mount
  useEffect(() => {
    initializeHeapAnalytics();
  }, []);

  return (
    <ChakraProvider theme={theme}>
      <NexusProvider theme={{ name: 'light' }}>
        {({ theme }) => (
          <div className={theme.className}>
            <BrowserRouter>
              {/* Handle token refresh and cookie processing first */}
              <TokenRefreshHandler />
              {/* Initialize user data from localStorage or JWT token */}
              <UserInitializer />
              {/* Identify the user to LaunchDarkly */}
              <LaunchDarklyUserIdentifier />
              {/* Identify the user to Heap Analytics */}
              <HeapUserIdentifier />
              {/* Track page navigation events */}
              <PageNavigationTracker />
              <Routes>
                <Route path="/login/*" element={<Login />}></Route>
                <Route path="/register/nra" element={<NraRegister />}></Route>
                <Route path="/register/*" element={<Register />}></Route>
                <Route
                  path="/reset_password/*"
                  element={<ResetPassword />}
                ></Route>
                {/* Conditional routing based on showReskin feature flag */}
                {showReskin ? (
                  // Nested routes for reskinned experience using ReskinnedDashboard layout
                  <>
                    <Route
                      path="/"
                      element={<NavigateWithQuery to="/overview" replace />}
                    ></Route>
                    <Route path="/*" element={<ReskinnedDashboard />}>
                      {/* Generate routes from configuration */}
                      {reskinnedRoutes.map(({ path, view }) => (
                        <Route
                          key={path}
                          path={path}
                          element={<WidgetDrawerWrapper view={view} />}
                        />
                      ))}
                      {/* Default redirects for reskinned experience */}
                      <Route
                        path="dashboard"
                        element={<NavigateWithQuery to="/overview" replace />}
                      />
                      <Route
                        path="Dash"
                        element={<NavigateWithQuery to="/overview" replace />}
                      />
                      <Route
                        index
                        element={<NavigateWithQuery to="/overview" replace />}
                      />
                    </Route>
                  </>
                ) : (
                  // Original route for non-reskinned experience
                  <>
                    <Route path="/" element={<Navigate to="/login/" />}></Route>
                    <Route path="/Dash" element={<Dashboard />}></Route>
                  </>
                )}

                <Route path="/callback" element={<CallbackHandler />}></Route>
                <Route
                  path="/okta/callback"
                  element={<OktaCallbackHandler />}
                ></Route>
                <Route
                  path="/ai_ondemand"
                  element={<AISummaryOneDemand />}
                ></Route>
                <Route
                  path="/registration-confirmation"
                  element={<RegistrationConfirmation />}
                ></Route>
              </Routes>
            </BrowserRouter>
          </div>
        )}
      </NexusProvider>
    </ChakraProvider>
  );
}

export default App;
